<template>
  <FlashNotifications ref="flash" />

  <!-- Desktop -->
  <div class="hidden sm:flex sm:flex-col bg-gray-50 dark:bg-[#0d1117]" style="height: calc(100vh - 57px)">

    <!-- Header -->
    <div class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center justify-between px-6 py-3.5 gap-4">
        <div>
          <h1 class="text-xl font-bold text-gray-900 dark:text-white leading-none">Global IT Components</h1>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Catalog entries every organization can attach a component to. Approve or reject org submitted entries to moderate the shared catalog.
          </p>
        </div>
        <div class="flex items-center gap-2 flex-shrink-0">
          <!-- Shown whenever there's something to review, or while its own
               filter is active (so it stays clickable to turn back off even
               if the count it started from has since dropped to 0). -->
          <button v-if="counts.pending > 0 || status === 'pending'" type="button" @click="togglePendingComponentsFilter"
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg transition-colors"
            :class="status === 'pending'
              ? 'bg-amber-600 text-white'
              : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30'">
            Pending Components
            <span class="min-w-[20px] h-5 inline-flex items-center justify-center rounded-full text-[11px] font-bold px-1"
              :class="status === 'pending' ? 'bg-white/20 text-white' : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400'">
              {{ counts.pending }}
            </span>
          </button>
          <button v-if="counts.pending_versions > 0 || hasPendingVersions" type="button" @click="togglePendingVersionsFilter"
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg transition-colors"
            :class="hasPendingVersions
              ? 'bg-amber-600 text-white'
              : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30'">
            Pending Versions
            <span class="min-w-[20px] h-5 inline-flex items-center justify-center rounded-full text-[11px] font-bold px-1"
              :class="hasPendingVersions ? 'bg-white/20 text-white' : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400'">
              {{ counts.pending_versions }}
            </span>
          </button>
          <button type="button" @click="openAdd"
            class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex-shrink-0">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
            </svg>
            Add Component
          </button>
        </div>
      </div>

      <!-- Filters -->
      <div class="flex items-center gap-2.5 px-6 pb-3.5">
        <div class="relative flex-1 max-w-xs">
          <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
          </svg>
          <input v-model="search" @input="onSearchInput" placeholder="Search name, vendor or type…"
            class="pl-9 pr-9 py-2 w-full text-sm bg-gray-50 dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" />
          <button v-if="search" type="button" @click="clearSearch"
            class="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <select v-model="typeFilter" class="text-sm bg-gray-50 dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All types</option>
          <option v-for="opt in TYPE_OPTIONS" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>

        <!-- Category multiselect (IT Component / Application) — both selected by default -->
        <div class="relative flex-shrink-0" ref="contextDropdownRef">
          <button type="button" @click="contextDropdownOpen = !contextDropdownOpen"
            class="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition">
            {{ contextTypeSummary }}
            <svg class="w-3.5 h-3.5 text-gray-400 transition-transform" :class="contextDropdownOpen ? 'rotate-180' : ''" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
            </svg>
          </button>
          <div v-if="contextDropdownOpen" class="absolute left-0 z-20 mt-1.5 w-44 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1">
            <label v-for="opt in CONTEXT_TYPE_OPTIONS" :key="opt.value"
              class="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
              <input type="checkbox" :checked="contextTypes.includes(opt.value)" @change="toggleContextType(opt.value)"
                class="w-3.5 h-3.5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-0" />
              {{ opt.label }}
            </label>
          </div>
        </div>

        <!-- Status single-select dropdown — shows the active filter's count, all statuses selectable, "All" by default -->
        <div class="relative ml-auto flex-shrink-0" ref="statusDropdownRef">
          <button type="button" @click="statusDropdownOpen = !statusDropdownOpen"
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-50 dark:bg-[#0d1117] text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 transition">
            {{ activeStatusTab.label }}
            <span class="min-w-[18px] h-[18px] rounded-full text-[10px] font-bold flex items-center justify-center px-1 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              {{ counts[activeStatusTab.countKey] }}
            </span>
            <svg class="w-3 h-3 text-gray-400 transition-transform" :class="statusDropdownOpen ? 'rotate-180' : ''" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
            </svg>
          </button>
          <div v-if="statusDropdownOpen" class="absolute right-0 z-20 mt-1.5 w-40 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1">
            <button v-for="tab in statusTabs" :key="tab.value" type="button" @click="selectStatus(tab.value)"
              class="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs font-semibold transition"
              :class="status === tab.value
                ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'">
              {{ tab.label }}
              <span class="min-w-[18px] h-[18px] rounded-full text-[10px] font-bold flex items-center justify-center px-1"
                :class="status === tab.value ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'">
                {{ counts[tab.countKey] }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Table area -->
    <div class="flex-1 overflow-y-auto">
      <!-- Skeleton -->
      <div v-if="loading" class="animate-pulse p-6 space-y-3">
        <div class="h-10 bg-gray-100 dark:bg-gray-800 rounded-xl w-full"></div>
        <div v-for="n in 8" :key="n" class="flex items-center gap-4 px-4 py-3 bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
          <div class="flex-1 space-y-1.5">
            <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-40"></div>
            <div class="h-2.5 bg-gray-100 dark:bg-gray-800 rounded w-56"></div>
          </div>
          <div class="h-5 w-20 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
          <div class="h-5 w-16 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
          <div class="h-3 w-24 bg-gray-100 dark:bg-gray-800 rounded"></div>
          <div class="h-7 w-20 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="flex flex-col items-center justify-center py-32 text-center px-4">
        <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
          <svg class="w-7 h-7 text-red-500" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
          </svg>
        </div>
        <h3 class="text-base font-semibold text-gray-900 dark:text-white">Something went wrong</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ error }}</p>
        <button @click="retry" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">Try again</button>
      </div>

      <!-- Table -->
      <div v-else class="p-6">
        <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-gray-100 dark:border-gray-700">
                <th class="text-left pl-6 pr-4 py-3 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                  <button type="button" @click="sortByColumn('name')"
                    class="inline-flex items-center gap-1 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                    Component
                    <svg class="w-2.5 h-2.5 flex-shrink-0" :class="sortBy === 'name' ? 'text-gray-600 dark:text-gray-300' : 'text-gray-300 dark:text-gray-600'"
                      fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24">
                      <path v-if="sortBy === 'name' && sortOrder === 'ASC'" stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5"/>
                      <path v-else stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
                    </svg>
                  </button>
                </th>
                <th class="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Category</th>
                <th class="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                  <button type="button" @click="sortByColumn('versions_count')"
                    class="inline-flex items-center gap-1 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                    Versions
                    <svg class="w-2.5 h-2.5 flex-shrink-0" :class="sortBy === 'versions_count' ? 'text-gray-600 dark:text-gray-300' : 'text-gray-300 dark:text-gray-600'"
                      fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24">
                      <path v-if="sortBy === 'versions_count' && sortOrder === 'ASC'" stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5"/>
                      <path v-else stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
                    </svg>
                  </button>
                </th>
                <th class="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                  <button type="button" @click="sortByColumn('organizations_count')"
                    class="inline-flex items-center gap-1 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                    Organizations
                    <svg class="w-2.5 h-2.5 flex-shrink-0" :class="sortBy === 'organizations_count' ? 'text-gray-600 dark:text-gray-300' : 'text-gray-300 dark:text-gray-600'"
                      fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24">
                      <path v-if="sortBy === 'organizations_count' && sortOrder === 'ASC'" stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5"/>
                      <path v-else stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
                    </svg>
                  </button>
                </th>
                <th class="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Status</th>
                <th class="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider hidden xl:table-cell">Submitted by</th>
                <th class="text-left px-4 py-3 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider hidden xl:table-cell">
                  <button type="button" @click="sortByColumn('created_at')"
                    class="inline-flex items-center gap-1 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                    Date
                    <svg class="w-2.5 h-2.5 flex-shrink-0" :class="sortBy === 'created_at' ? 'text-gray-600 dark:text-gray-300' : 'text-gray-300 dark:text-gray-600'"
                      fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24">
                      <path v-if="sortBy === 'created_at' && sortOrder === 'ASC'" stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5"/>
                      <path v-else stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
                    </svg>
                  </button>
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-50 dark:divide-gray-700/60">
              <template v-for="c in records" :key="c.id">
              <tr class="hover:bg-gray-50/60 dark:hover:bg-gray-700/20 transition-colors cursor-pointer" @click="openEdit(c)">
                <td class="pl-6 pr-2 py-3.5">
                  <div class="flex items-center gap-2.5">
                    <ItComponentLogo :name="c.name" :logo-url="c.logo_url" size="w-6 h-6" initial-text-size="text-[10px]" image-padding="p-0.5" />
                    <div class="min-w-0">
                      <div class="flex items-center gap-2">
                        <p class="font-medium text-gray-800 dark:text-gray-100">{{ c.name }}</p>
                        <span class="inline-flex text-[11px] font-semibold rounded-full px-2.5 py-1" :class="typeBadgeClass(c.type)">
                          {{ typeLabel(c.type) }}
                        </span>
                      </div>
                      <span class="text-xs text-gray-500 dark:text-gray-400">{{ c.vendor || '—' }}</span>
                    </div>
                  </div>
                </td>
                <td class="px-4 py-3.5 whitespace-nowrap">
                  <span class="inline-flex text-[11px] font-semibold rounded-full px-2.5 py-1" :class="contextTypeBadgeClass(c.context_type)">
                    {{ contextTypeLabel(c.context_type) }}
                  </span>
                </td>
                <td class="px-4 py-3.5 whitespace-nowrap">
                  <span class="text-xs font-medium text-gray-700 dark:text-gray-300">{{ versionCounts(c).total }}</span>
                  <span v-if="versionCounts(c).pending > 0"
                    class="ml-1.5 inline-flex text-[10px] font-semibold rounded-full px-2 py-0.5 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400">
                    {{ versionCounts(c).pending }} pending
                  </span>
                </td>
                <td class="px-4 py-3.5 whitespace-nowrap">
                  <span class="text-xs font-medium text-gray-700 dark:text-gray-300">{{ c.organizations_count ?? 0 }}</span>
                </td>
                <td class="px-4 py-3.5">
                  <!-- Read-only — approving/rejecting now lives in the Edit
                       slide-over, not as a standing control on the table row. -->
                  <span class="inline-flex text-xs font-semibold rounded-full px-2.5 py-1" :class="statusBadgeClass(c.status)">
                    {{ STATUS_LABELS[c.status] }}
                  </span>
                </td>
                <td class="px-4 py-3.5 hidden xl:table-cell">
                  <span class="text-xs text-gray-500 dark:text-gray-400">{{ c.added_by_org_name || 'Curated' }}</span>
                </td>
                <td class="px-4 py-3.5 hidden xl:table-cell">
                  <span class="text-xs text-gray-500 dark:text-gray-400">{{ formatDate(c.created_at) }}</span>
                </td>
              </tr>
              </template>

              <!-- Empty -->
              <tr v-if="!records.length">
                <td colspan="7">
                  <div class="flex flex-col items-center justify-center py-20 text-center">
                    <div class="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
                      <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375"/>
                      </svg>
                    </div>
                    <p class="text-sm font-medium text-gray-500 dark:text-gray-400">No catalog entries found</p>
                    <p v-if="search" class="text-xs text-gray-400 dark:text-gray-500 mt-1">Try a different search</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="flex items-center justify-between mt-4 px-1">
          <span class="text-xs text-gray-400 dark:text-gray-500">
            Page {{ page.current_page }} of {{ page.total_pages }} · {{ page.total_records }} total
          </span>
          <div class="flex gap-1.5">
            <button @click="prevPage" :disabled="page.current_page <= 1"
              class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition disabled:opacity-30 disabled:cursor-not-allowed"
              :class="page.current_page <= 1
                ? 'border-gray-200 dark:border-gray-700 text-gray-400'
                : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
              </svg>
              Prev
            </button>
            <button @click="nextPage" :disabled="page.current_page >= page.total_pages"
              class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition disabled:opacity-30 disabled:cursor-not-allowed"
              :class="page.current_page >= page.total_pages
                ? 'border-gray-200 dark:border-gray-700 text-gray-400'
                : 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700'">
              Next
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Mobile -->
  <div class="sm:hidden bg-gray-50 dark:bg-[#0d1117] min-h-screen">
    <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
      <div class="flex items-center justify-between px-4 h-14">
        <h1 class="text-[22px] font-bold text-gray-900 dark:text-white tracking-tight leading-none">IT Components</h1>
        <button type="button" @click="openAdd"
          class="w-8 h-8 flex items-center justify-center rounded-full bg-blue-600 text-white flex-shrink-0">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
          </svg>
        </button>
      </div>
      <div class="flex items-center gap-1.5 px-4 pb-2 overflow-x-auto">
        <button v-for="tab in statusTabs" :key="tab.value" @click="setStatus(tab.value)"
          class="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition"
          :class="status === tab.value ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'">
          {{ tab.label }} ({{ counts[tab.countKey] }})
        </button>
      </div>
      <div class="px-4 pb-3">
        <div class="relative">
          <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
          </svg>
          <input v-model="search" @input="onSearchInput" placeholder="Search…"
            class="w-full pl-9 pr-9 py-2 text-sm bg-gray-50 dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <button v-if="search" type="button" @click="clearSearch"
            class="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <div class="pb-24 pt-3 px-4 space-y-2">
      <div v-for="c in records" :key="c.id" @click="openEdit(c)"
        class="bg-white dark:bg-[#1C2128] rounded-2xl border border-gray-100 dark:border-gray-700 p-4 cursor-pointer">
        <div class="flex items-start justify-between gap-2">
          <div class="flex items-start gap-2.5 min-w-0">
            <ItComponentLogo :name="c.name" :logo-url="c.logo_url" size="w-7 h-7" initial-text-size="text-[11px]" image-padding="p-0.5" class="mt-0.5" />
            <div class="min-w-0">
              <div class="flex items-center gap-1.5">
                <p class="font-semibold text-sm text-gray-800 dark:text-gray-100 truncate">{{ c.name }}</p>
                <span class="inline-flex flex-shrink-0 text-[10px] font-semibold rounded-full px-2 py-0.5" :class="typeBadgeClass(c.type)">
                  {{ typeLabel(c.type) }}
                </span>
              </div>
              <p class="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{{ c.vendor || '—' }}</p>
            </div>
          </div>
          <!-- Read-only — approving/rejecting now lives in the Edit slide-over. -->
          <span class="flex-shrink-0 inline-flex text-[11px] font-semibold rounded-full px-2.5 py-1" :class="statusBadgeClass(c.status)">
            {{ STATUS_LABELS[c.status] }}
          </span>
        </div>
        <div class="flex items-center gap-1.5 mt-2">
          <span class="text-[11px] text-gray-400 dark:text-gray-500">{{ c.added_by_org_name || 'Curated' }}</span>
          <span class="text-[11px] text-gray-400 dark:text-gray-500">
            · {{ versionCounts(c).total }} {{ versionCounts(c).total === 1 ? 'version' : 'versions' }}
          </span>
          <span v-if="versionCounts(c).pending > 0"
            class="inline-flex text-[10px] font-semibold rounded-full px-2 py-0.5 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400">
            {{ versionCounts(c).pending }} pending
          </span>
          <span class="text-[11px] text-gray-400 dark:text-gray-500">
            · {{ c.organizations_count ?? 0 }} {{ (c.organizations_count ?? 0) === 1 ? 'organization' : 'organizations' }}
          </span>
        </div>
        <div class="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          <span class="text-[11px] text-gray-400 dark:text-gray-500">{{ formatDate(c.created_at) }}</span>
        </div>
      </div>

      <div v-if="!records.length && !loading" class="flex flex-col items-center py-16 text-center">
        <p class="text-sm text-gray-400 dark:text-gray-500">No catalog entries found</p>
      </div>

      <div class="flex items-center justify-between pt-2">
        <span class="text-xs text-gray-400 dark:text-gray-500">Page {{ page.current_page }} of {{ page.total_pages }}</span>
        <div class="flex gap-2">
          <button @click="prevPage" :disabled="page.current_page <= 1" class="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 disabled:opacity-30">Prev</button>
          <button @click="nextPage" :disabled="page.current_page >= page.total_pages" class="px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white disabled:opacity-30">Next</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Add/Edit slide-over -->
  <AddGlobalComponentSlideOver
    :open="modal.open"
    :title="modal.title"
    :submit-label="modal.editing ? 'Save' : 'Add'"
    :saving="modal.saving"
    :error="modal.error"
    :initial-name="modal.editing?.name || ''"
    :initial-type="modal.editing?.type || ''"
    :initial-vendor="modal.editing?.vendor || ''"
    :initial-description="modal.editing?.description || ''"
    :initial-context-type="modal.editing?.context_type || 'ItComponent'"
    :initial-logo-url="modal.editing?.logo_url || ''"
    :global-component-id="modal.editing?.id || ''"
    :status="modal.editing?.status || 'pending'"
    :status-working="!!modal.editing && working === modal.editing.id"
    :highlight-versions="!!modal.editing && justCreatedComponentId === modal.editing.id"
    @close="closeModal"
    @submit="submitModal"
    @logo-updated="onLogoUpdated"
    @change-status="(newStatus) => changeStatus(modal.editing, newStatus)"
    @version-status-changed="onVersionStatusChanged"
    @notify="notify" />
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useAdminGlobalComponentsStore } from 'stores/admin_global_components.js'
import { TYPE_OPTIONS, typeLabel, typeBadgeClass, CONTEXT_TYPE_OPTIONS, contextTypeLabel, contextTypeBadgeClass } from 'controllers/vue_components/organizations/it_components/itComponentOptions.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import AddGlobalComponentSlideOver from 'controllers/vue_components/admin/global_components/AddGlobalComponentSlideOver.vue'
import ItComponentLogo from 'controllers/vue_components/organizations/it_components/ItComponentLogo.vue'
import { useAdminGlobalComponentVersionsStore } from 'stores/admin_global_component_versions.js'

const flash = ref(null)
const store = useAdminGlobalComponentsStore()
const versionsStore = useAdminGlobalComponentVersionsStore()

// Server-provided counts (versions_count/pending_versions_count from the
// index) can go stale once the row's versions panel is open and the admin
// adds versions or flips statuses — so once that panel has actually loaded,
// derive the counts live from its records instead.
function versionCounts(c) {
  const entry = versionsStore.byComponent[c.id]
  if (entry?.fetchedAt) {
    return {
      total: entry.records.length,
      pending: entry.records.filter((v) => v.status === 'pending').length,
    }
  }
  return { total: c.versions_count ?? 0, pending: c.pending_versions_count ?? 0 }
}

const statusTabs = [
  { value: '', label: 'All', countKey: 'total' },
  { value: 'pending', label: 'Pending', countKey: 'pending' },
  { value: 'approved', label: 'Approved', countKey: 'approved' },
  { value: 'rejected', label: 'Rejected', countKey: 'rejected' },
  { value: 'org_specific', label: 'Org specific', countKey: 'org_specific' },
]

// Read-only badge on the table/card — approving/rejecting now lives in the
// Edit slide-over (see AddGlobalComponentSlideOver's Status field), not as a
// standing control here.
const STATUS_LABELS = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected', org_specific: 'Org specific' }
const STATUS_BADGE_CLASSES = {
  pending: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
  approved: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400',
  rejected: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
  org_specific: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
}
function statusBadgeClass(status) {
  return STATUS_BADGE_CLASSES[status] ?? 'bg-gray-100 dark:bg-gray-800 text-gray-500'
}

const working = ref(null)
const modal = ref({ open: false, editing: null, title: '', saving: false, error: '' })

// Set right after a fresh Add succeeds (see submitModal) so the slide-over's
// Versions section can call out that it's now usable, since the panel stays
// open at that point rather than closing — see submitModal's own comment.
// Cleared on a timeout, or naturally on the next open since a new modal
// object always replaces this one. Mirrors OrgComponentsPanel.vue's
// identical justCreatedComponentId pattern.
const justCreatedComponentId = ref(null)
let justCreatedTimeout = null

const statusDropdownOpen = ref(false)
const statusDropdownRef = ref(null)
const contextDropdownOpen = ref(false)
const contextDropdownRef = ref(null)

function onClickOutside(e) {
  if (statusDropdownRef.value && !statusDropdownRef.value.contains(e.target)) {
    statusDropdownOpen.value = false
  }
  if (contextDropdownRef.value && !contextDropdownRef.value.contains(e.target)) {
    contextDropdownOpen.value = false
  }
}
onMounted(() => document.addEventListener('click', onClickOutside, true))
onUnmounted(() => {
  document.removeEventListener('click', onClickOutside, true)
  clearTimeout(justCreatedTimeout)
})

// Relays flash messages emitted from the slide-over's Versions section (it
// has no flash of its own).
function notify(message, type) {
  flash.value?.show(message, type)
}

const loading = computed(() => store.loading)
const error = computed(() => store.error)
const records = computed(() => store.records)
const page = computed(() => store.page)
const counts = computed(() => store.counts)
const status = computed(() => store.status)

const search = computed({
  get: () => store.search,
  set: (val) => store.setSearch(val),
})
const typeFilter = computed({
  get: () => store.type,
  set: (val) => store.setType(val),
})
const contextTypes = computed(() => store.contextTypes)
const contextTypeSummary = computed(() => {
  if (contextTypes.value.length === CONTEXT_TYPE_OPTIONS.length) return 'All categories'
  return CONTEXT_TYPE_OPTIONS.filter((o) => contextTypes.value.includes(o.value)).map((o) => o.label).join(', ')
})
function toggleContextType(value) {
  const current = contextTypes.value
  if (current.includes(value)) {
    if (current.length === 1) return // at least one category must stay selected
    store.setContextTypes(current.filter((v) => v !== value))
  } else {
    store.setContextTypes([...current, value])
  }
  store.fetchList()
}
const sortBy = computed(() => store.sortBy)
const sortOrder = computed(() => store.sortOrder)

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

let searchTimeout = null
function onSearchInput() {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => store.fetchList(), 300)
}

function clearSearch() {
  clearTimeout(searchTimeout)
  search.value = ''
  store.fetchList()
}

function setStatus(val) {
  store.setStatus(val)
  store.fetchList()
}

// Toggles — clicking again (or once the underlying count is resolved to 0
// while still active) turns the filter back off.
function togglePendingComponentsFilter() {
  setStatus(status.value === 'pending' ? '' : 'pending')
}

const hasPendingVersions = computed(() => store.hasPendingVersions)
function togglePendingVersionsFilter() {
  store.setHasPendingVersions(!hasPendingVersions.value)
  store.fetchList()
}

const activeStatusTab = computed(() => statusTabs.find((tab) => tab.value === status.value) ?? statusTabs[0])

function selectStatus(val) {
  setStatus(val)
  statusDropdownOpen.value = false
}

function toggleSortOrder() {
  store.setSort(store.sortBy, store.sortOrder === 'DESC' ? 'ASC' : 'DESC')
}

// Column header click — sorts by that column, or flips direction if it's
// already the active sort. store.sortBy/sortOrder changes are picked up by
// the watch below, which refetches.
function sortByColumn(field) {
  if (store.sortBy === field) toggleSortOrder()
  else store.setSort(field, 'DESC')
}

function nextPage() {
  if (store.page.current_page < store.page.total_pages) {
    store.goToPage(store.page.current_page + 1)
    store.fetchList()
  }
}
function prevPage() {
  if (store.page.current_page > 1) {
    store.goToPage(store.page.current_page - 1)
    store.fetchList()
  }
}

const STATUS_ACTIONS = {
  pending: (id) => store.unapprove(id),
  approved: (id) => store.approve(id),
  rejected: (id) => store.reject(id),
  org_specific: (id) => store.markOrgSpecific(id),
}
const STATUS_VERBS = {
  pending: 'reset to pending', approved: 'approved', rejected: 'rejected', org_specific: 'marked org specific',
}

// Called from the Edit slide-over's Status field (see AddGlobalComponentSlideOver).
// Fires immediately on select, same as version status changes — a separate
// approve/reject/unapprove endpoint, not bundled into the regular update payload.
async function changeStatus(c, newStatus) {
  if (newStatus === c.status) return

  working.value = c.id
  try {
    const res = await STATUS_ACTIONS[newStatus](c.id)
    if (res.js_error || !res.response.success) {
      flash.value?.show(res.error?.data?.message ?? 'Something went wrong.', 'alert')
    } else {
      flash.value?.show(`${c.name} ${STATUS_VERBS[newStatus]}.`, 'notice')
      // The store's own records list updates itself (applyRecord), but
      // modal.editing is this page's own separate local copy — keep it in
      // sync too so the slide-over's Status field reflects the change right away.
      if (modal.value.editing?.id === c.id) modal.value.editing.status = newStatus
    }
  } finally {
    working.value = null
  }
}

function openAdd() {
  modal.value = { open: true, editing: null, title: 'Add Catalog Entry', saving: false, error: '' }
}

function openEdit(c) {
  modal.value = { open: true, editing: c, title: 'Edit Catalog Entry', saving: false, error: '' }
}

function closeModal() {
  modal.value.open = false
}

function modalErrorMessage(res) {
  return res.error?.data?.data?.join(', ') || res.error?.data?.message || 'Something went wrong.'
}

async function submitModal(payload) {
  modal.value.saving = true
  modal.value.error = ''

  const { editing } = modal.value
  const res = editing ? await store.update(editing.id, payload) : await store.create(payload)

  modal.value.saving = false

  if (res.js_error || !res.response.success) {
    modal.value.error = modalErrorMessage(res)
    return
  }

  if (editing) {
    modal.value.open = false
    flash.value?.show('Catalog entry updated.', 'notice')
  } else {
    // Stay open, switched into edit mode for the newly created entry — lets
    // the slide-over's Versions section (and logo uploader) appear, both of
    // which need a real id, so the admin can keep going without having to
    // close and reopen via the row's own Edit button. Closing from here on
    // is a deliberate Cancel/X, not an automatic side effect of the first Save.
    modal.value.editing = res.response.data
    modal.value.title = 'Edit Catalog Entry'
    flash.value?.show('Catalog entry added.', 'notice')

    clearTimeout(justCreatedTimeout)
    justCreatedComponentId.value = res.response.data.id
    justCreatedTimeout = setTimeout(() => { justCreatedComponentId.value = null }, 6000)
  }
}

// AddGlobalComponentSlideOver's AvatarUploadCard hits the logo endpoints
// directly (it isn't routed through the store) — merge its fresh record back
// into modal.editing so the slide-over's preview picks up the new logo_url.
function onLogoUpdated(updatedComponent) {
  if (!modal.value.editing) return
  modal.value.editing = { ...modal.value.editing, ...updatedComponent }
}

// Keeps the header's "Pending Versions" count in sync the moment a version
// is approved/rejected/reset inside the slide-over, without waiting for the
// next full list refetch (which wouldn't happen until the panel closes anyway).
function onVersionStatusChanged({ previousStatus, newStatus }) {
  if (previousStatus === 'pending' && newStatus !== 'pending') store.counts.pending_versions -= 1
  else if (previousStatus !== 'pending' && newStatus === 'pending') store.counts.pending_versions += 1
}

function retry() {
  store.invalidate()
  store.fetchList()
}

watch([sortBy, sortOrder, typeFilter], () => store.fetchList())

onMounted(() => store.fetchIfStale())
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
</style>
